const RANDOM_AVATAR_CIRCLES_SELECTOR = "[data-hero-random-avatar-circles]";
const RANDOM_AVATAR_SELECTOR = "[data-hero-random-avatar]";
const AVATAR_CIRCLES_SELECTOR =
	".hero-centered-decor--avatars, .hero-contact-decor--avatars";
const CENTERED_REVEAL_SELECTOR = "[data-hero-centered-reveal]";
const CENTERED_REVEAL_PENDING_CLASS = "hero--centered-reveal-pending";
const CENTERED_REVEAL_VISIBLE_CLASS = "hero--centered-reveal-visible";
const PROXIMITY_RADIUS = 180;
const PROXIMITY_STRENGTH = 10;

function shuffle(items) {
	const shuffled = [...items];

	for (let index = shuffled.length - 1; index > 0; index -= 1) {
		const randomIndex = Math.floor(Math.random() * (index + 1));
		[shuffled[index], shuffled[randomIndex]] = [
			shuffled[randomIndex],
			shuffled[index],
		];
	}

	return shuffled;
}

export function initHeroDecor() {
	initCenteredHeroReveals();
	initCenteredAvatarProximity();

	for (const decor of document.querySelectorAll(RANDOM_AVATAR_CIRCLES_SELECTOR)) {
		const circles = [...decor.querySelectorAll(":scope > span")];
		const avatars = circles.flatMap((circle) => [
			...circle.querySelectorAll(RANDOM_AVATAR_SELECTOR),
		]);

		if (!circles.length || !avatars.length) {
			continue;
		}

		const randomizedCircles = shuffle(circles);

		for (const avatar of avatars) {
			avatar.remove();
		}

		avatars.forEach((avatar, index) => {
			randomizedCircles[index]?.append(avatar);
		});

		initAvatarProximity(circles.filter((circle) => circle.querySelector(RANDOM_AVATAR_SELECTOR)));
	}
}

function initCenteredAvatarProximity() {
	for (const decor of document.querySelectorAll(AVATAR_CIRCLES_SELECTOR)) {
		const circles = [...decor.querySelectorAll(":scope > span")].filter((circle) =>
			circle.querySelector("img"),
		);

		initAvatarProximity(circles);
	}
}

function initCenteredHeroReveals() {
	const heroes = document.querySelectorAll(CENTERED_REVEAL_SELECTOR);

	if (!heroes.length) {
		return;
	}

	if (!("IntersectionObserver" in window)) {
		for (const hero of heroes) {
			hero.classList.add(CENTERED_REVEAL_VISIBLE_CLASS);
		}

		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (!entry.isIntersecting) {
					continue;
				}

				entry.target.classList.add(CENTERED_REVEAL_VISIBLE_CLASS);
				observer.unobserve(entry.target);
			}
		},
		{
			threshold: 0.2,
		},
	);

	for (const hero of heroes) {
		hero.classList.add(CENTERED_REVEAL_PENDING_CLASS);
		observer.observe(hero);
	}
}

function initAvatarProximity(circles) {
	if (
		!circles.length ||
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	) {
		return;
	}

	let pointer = null;
	let frameId = null;

	const resetCircles = () => {
		for (const circle of circles) {
			circle.style.removeProperty("--hero-avatar-shift-x");
			circle.style.removeProperty("--hero-avatar-shift-y");
		}
	};

	const sync = () => {
		frameId = null;

		if (!pointer) {
			resetCircles();
			return;
		}

		for (const circle of circles) {
			const rect = circle.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;
			const deltaX = centerX - pointer.x;
			const deltaY = centerY - pointer.y;
			const distance = Math.hypot(deltaX, deltaY);
			const influence = Math.max(0, 1 - distance / PROXIMITY_RADIUS);

			if (influence === 0 || distance === 0) {
				circle.style.setProperty("--hero-avatar-shift-x", "0px");
				circle.style.setProperty("--hero-avatar-shift-y", "0px");
				continue;
			}

			const shift = influence * PROXIMITY_STRENGTH;

			circle.style.setProperty(
				"--hero-avatar-shift-x",
				`${(deltaX / distance) * shift}px`,
			);
			circle.style.setProperty(
				"--hero-avatar-shift-y",
				`${(deltaY / distance) * shift}px`,
			);
		}
	};

	const scheduleSync = () => {
		if (frameId) {
			return;
		}

		frameId = window.requestAnimationFrame(sync);
	};

	window.addEventListener(
		"pointermove",
		(event) => {
			pointer = { x: event.clientX, y: event.clientY };
			scheduleSync();
		},
		{ passive: true },
	);
	window.addEventListener(
		"pointerleave",
		() => {
			pointer = null;
			scheduleSync();
		},
		{ passive: true },
	);
}
